import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, FormHelperText, Grid } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';

import { createLab, Lab, provisionLabCustomerBucket } from 'api/labs';
import { isNonProductionEnvironment } from 'utils/deploymentEnvironment';
import { getQueryErrorMessage } from 'utils/httpError';
import queryClient from 'utils/queryClient';
import { labsQueryKey } from 'utils/queryHooks/useLabs';
import { LabFormFields, LabFormValues } from './LabFormFields';
import { normalizeLabNames, parseLabelSettings } from './formUtils';

interface PartialProvisioningState {
  errorDetail: string;
  labId: string;
}

interface CreatedLabState {
  customerBucketName?: string | null;
  labId: string;
}

const ProvisioningErrorDetails: React.FC<{ detail: string }> = ({ detail }) => {
  return (
    <Box component="span" sx={{ display: 'block', mt: 0.5, wordBreak: 'break-word' }}>
      Details: {detail}
    </Box>
  );
};

export const CreateLabForm: React.FC<{ existingLabs: Lab[] }> = ({ existingLabs }) => {
  const [createdLab, setCreatedLab] = useState<CreatedLabState | null>(null);
  const [partialProvisioningState, setPartialProvisioningState] = useState<PartialProvisioningState | null>(null);
  const nonProductionEnvironmentEnabled = isNonProductionEnvironment();
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<LabFormValues>({
    defaultValues: {
      labName: '',
      labelSettings: '',
      isStagingLab: Boolean(nonProductionEnvironmentEnabled),
      isTestLab: false,
    },
    mode: 'onChange',
  });

  const createLabMutation = useMutation(createLab, {
    onSuccess: (response) => {
      if (response.bucketProvisioned) {
        setCreatedLab({
          labId: response.labId,
          customerBucketName: response.customerBucketName,
        });
        setPartialProvisioningState(null);
      } else {
        setCreatedLab(null);
        setPartialProvisioningState({
          labId: response.labId,
          errorDetail: response.bucketProvisioningError || 'Bucket provisioning failed.',
        });
      }
      queryClient.invalidateQueries(labsQueryKey);
    },
  });

  const retryProvisioningMutation = useMutation(provisionLabCustomerBucket, {
    onSuccess: (response) => {
      setCreatedLab({
        labId: response.labId,
        customerBucketName: response.customerBucketName,
      });
      setPartialProvisioningState(null);
      queryClient.invalidateQueries(labsQueryKey);
    },
  });

  const labName = watch('labName');
  const isStagingLab = watch('isStagingLab');
  const normalizedExistingLabNames = normalizeLabNames(existingLabs.map((lab) => lab.name));
  const isSubmitting = createLabMutation.isLoading || retryProvisioningMutation.isLoading;

  const clearSubmissionState = () => {
    reset();
    setCreatedLab(null);
    setPartialProvisioningState(null);
    createLabMutation.reset();
    retryProvisioningMutation.reset();
  };

  const onSubmit = (values: LabFormValues) => {
    setCreatedLab(null);
    setPartialProvisioningState(null);
    createLabMutation.reset();
    retryProvisioningMutation.reset();

    const labelSettings = parseLabelSettings(values.labelSettings);

    createLabMutation.mutate({
      labName: values.labName,
      ...(nonProductionEnvironmentEnabled && values.isStagingLab ? { isStagingLab: true } : {}),
      ...(values.isTestLab ? { isTestLab: true } : {}),
      ...(labelSettings !== undefined ? { labelSettings } : {}),
    });
  };

  const handleRetryProvisioning = () => {
    if (!partialProvisioningState) {
      return;
    }

    retryProvisioningMutation.reset();
    retryProvisioningMutation.mutate(partialProvisioningState.labId, {
      onError: (error) => {
        setPartialProvisioningState({
          labId: partialProvisioningState.labId,
          errorDetail: getQueryErrorMessage(error, 'Bucket provisioning failed.'),
        });
      },
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)} data-testid="create-lab-form" sx={{ maxWidth: 480 }}>
      <Grid container direction="column" spacing={2}>
        {createdLab && (
          <Grid item>
            <Alert
              severity="success"
              data-testid="create-lab-success-alert"
              action={
                <Button color="inherit" size="small" onClick={clearSubmissionState}>
                  Create another
                </Button>
              }
            >
              Lab created successfully. New lab ID: <strong>{createdLab.labId}</strong>
              {createdLab.customerBucketName ? (
                <>
                  {' '}
                  Customer bucket: <strong>{createdLab.customerBucketName}</strong>
                </>
              ) : null}
            </Alert>
          </Grid>
        )}

        {partialProvisioningState && (
          <Grid item>
            <Alert
              severity="warning"
              data-testid="create-lab-partial-success-alert"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRetryProvisioning}
                  disabled={retryProvisioningMutation.isLoading}
                  data-testid="retry-provisioning-button"
                >
                  {retryProvisioningMutation.isLoading ? 'Retrying…' : 'Retry customer bucket provisioning'}
                </Button>
              }
            >
              Lab created, but customer bucket provisioning failed. Lab ID:{' '}
              <strong>{partialProvisioningState.labId}</strong>.
              <ProvisioningErrorDetails detail={partialProvisioningState.errorDetail} />
            </Alert>
          </Grid>
        )}

        {createLabMutation.isError && !partialProvisioningState && (
          <Grid item>
            <Alert severity="error" data-testid="create-lab-error-alert">
              Failed to create lab. {getQueryErrorMessage(createLabMutation.error, 'Check the inputs and try again.')}
            </Alert>
          </Grid>
        )}

        {retryProvisioningMutation.isError && partialProvisioningState && (
          <Grid item>
            <Alert severity="error" data-testid="retry-provisioning-error-alert">
              Retry failed.
              <ProvisioningErrorDetails detail={partialProvisioningState.errorDetail} />
            </Alert>
          </Grid>
        )}

        <LabFormFields
          disabled={isSubmitting}
          errors={errors}
          existingLabNames={normalizedExistingLabNames}
          labelSettingsHelperText="Leave blank to use the database default"
          register={register}
          labNameInputTestId="lab-name-input"
          labelSettingsInputTestId="label-settings-input"
        />
        {nonProductionEnvironmentEnabled && (
          <Grid item>
            <FormControlLabel
              control={
                <Checkbox disabled={isSubmitting} {...register('isStagingLab')} checked={Boolean(isStagingLab)} />
              }
              label="Use staging-specific storage for this lab"
            />
            <FormHelperText>
              Defaults to enabled in non-production deployments. Clear it to keep the production permanent storage for
              this lab.
            </FormHelperText>
            {isStagingLab && (
              <Alert severity="warning" data-testid="staging-lab-backend-warning" sx={{ mt: 1 }}>
                If this frontend is connected to a production backend, requests with this option enabled will fail.
              </Alert>
            )}
          </Grid>
        )}
        <Grid item>
          <Grid container spacing={1}>
            <Grid item>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting || !labName.trim() || Boolean(errors.labName)}
                data-testid="create-lab-submit-button"
                startIcon={isSubmitting ? <CircularProgress size={16} /> : undefined}
              >
                {isSubmitting ? 'Creating…' : 'Create Lab'}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};
